// Structured-data + SEO constants. Head tags are emitted by route meta() (see
// lib/meta.ts pageMeta); this module now only exports the pure schema builders
// and the shared site constants those routes consume. (The old react-helmet
// <SEO> component was removed once every route owned its own meta().)

export const SITE_URL = 'https://gazar.dev'
export const DEFAULT_IMAGE = 'https://assets.gazar.dev/gazar_dev_14ce4692e7.png'

export function personSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Ehsan Gazar',
    url: SITE_URL,
    image: DEFAULT_IMAGE,
    jobTitle: 'Principal Engineer',
    description: 'Principal Engineer with 16+ years shipping software. Writes about system design, TypeScript, and engineering leadership.',
    sameAs: [
      'https://twitter.com/ehsangazar',
      'https://github.com/ehsangazar',
      'https://linkedin.com/in/ehsangazar',
      'https://youtube.com/@ehsangazar',
    ],
  }
}

export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Gazar',
    url: SITE_URL,
    description: 'Building high-performing teams and products driven by quality. Writing about system design, TypeScript, and engineering leadership.',
    author: personSchema(),
  }
}

export function articleSchema(article: {
  title: string
  description: string
  path: string
  image?: string
  publishedAt?: string
  updatedAt?: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    url: `${SITE_URL}${article.path}`,
    image: article.image || DEFAULT_IMAGE,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt || article.publishedAt,
    author: personSchema(),
    publisher: {
      '@type': 'Person',
      name: 'Ehsan Gazar',
      url: SITE_URL,
    },
  }
}

export function courseSchema(course: {
  title: string
  description: string
  slug: string
  image?: string
  price?: number
  level?: string | null
}) {
  // Google's Course rich result needs an offer and at least one instance before it
  // will show anything. Without them the schema validates but never renders.
  const url = `${SITE_URL}/courses/${course.slug}`
  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: course.title,
    description: course.description,
    url,
    image: course.image || DEFAULT_IMAGE,
    ...(course.level ? { educationalLevel: course.level } : {}),
    provider: {
      '@type': 'Person',
      name: 'Ehsan Gazar',
      url: SITE_URL,
    },
    ...(course.price === undefined
      ? {}
      : {
          offers: {
            '@type': 'Offer',
            price: course.price,
            priceCurrency: 'USD',
            category: course.price === 0 ? 'Free' : 'Paid',
            availability: 'https://schema.org/InStock',
            url,
          },
          hasCourseInstance: {
            '@type': 'CourseInstance',
            courseMode: 'online',
            courseWorkload: 'PT12H',
            instructor: { '@type': 'Person', name: 'Ehsan Gazar', url: SITE_URL },
          },
        }),
  }
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
}
